import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { Flex, Segmented, Switch, Text } from '@nula/core-ui';
import type { RecordingSettings } from '../../hooks/use-recording-settings';
import styles from './meeting-automation-section.module.css';

export type MeetingAutomationSectionProps = {
  autoJoinEnabled: boolean;
  autoEndEnabled: boolean;
  /** Grace seconds before an end signal acts (15 / 30 / 60 / 120). */
  endGraceSecs: number;
  onUpdate: (patch: Partial<RecordingSettings>) => void;
  className?: string;
};

const root = cva(styles.root);
const toggle = cva(styles.toggle);
const toggleLabel = cva(styles.toggleLabel);

/** Discrete grace options (seconds) with human labels. `Segmented` is
 *  string-valued, so values are the second-count as a string and converted to
 *  a number at the onChange boundary. */
const GRACE_OPTIONS: { label: string; value: string }[] = [
  { label: '15s', value: '15' },
  { label: '30s', value: '30' },
  { label: '1m', value: '60' },
  { label: '2m', value: '120' },
];

/**
 * Meetings-tab automation controls: auto-join (calendar meetings), auto-end
 * (silent stop), and the end-grace timer that governs both how long we wait
 * before the "still recording?" prompt and the auto-end delay.
 */
export function MeetingAutomationSection({
  autoJoinEnabled,
  autoEndEnabled,
  endGraceSecs,
  onUpdate,
  className,
}: MeetingAutomationSectionProps) {
  return (
    <Flex vertical className={clsx(root(), className)}>
      <Flex className={toggle()}>
        <Flex vertical className={toggleLabel()}>
          <Text size="sm" className={styles.label}>Auto-join calendar meetings</Text>
          <Text type="secondary" size="xs">
            Start recording scheduled calendar meetings automatically, without asking.
          </Text>
        </Flex>
        <Switch
          checked={autoJoinEnabled}
          onChange={(v) => onUpdate({ autoJoinEnabled: v })}
          size="sm"
        />
      </Flex>

      <Flex className={toggle()}>
        <Flex vertical className={toggleLabel()}>
          <Text size="sm" className={styles.label}>Auto-end meetings</Text>
          <Text type="secondary" size="xs">
            Stop recording automatically when a meeting ends, instead of asking.
          </Text>
        </Flex>
        <Switch
          checked={autoEndEnabled}
          onChange={(v) => onUpdate({ autoEndEnabled: v })}
          size="sm"
        />
      </Flex>

      <Flex className={toggle()}>
        <Flex vertical className={toggleLabel()}>
          <Text size="sm" className={styles.label}>Wait before ending</Text>
          <Text type="secondary" size="xs">
            Grace period once a meeting looks over (plus a few seconds to detect it).
          </Text>
        </Flex>
        <Segmented
          size="sm"
          options={GRACE_OPTIONS}
          value={String(endGraceSecs)}
          onChange={(v) => onUpdate({ endGraceSecs: Number(v) })}
        />
      </Flex>
    </Flex>
  );
}
